import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  Card,
  CardBody,
  Text,
  Divider,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { Select } from '@chakra-ui/react';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phoneNumber: user?.phoneNumber || '',
    farmLocation: user?.farmLocation || '',
    farmSize: user?.farmSize || '',
    farmName: user?.farmName || '',
    cropCategories: user?.cropCategories || [],
  });
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('fullName', profileForm.fullName);
      formData.append('phoneNumber', profileForm.phoneNumber);
      if (user?.role === 'farmer') {
         formData.append('farmLocation', profileForm.farmLocation);
         formData.append('farmSize', profileForm.farmSize.toString());
         formData.append('farmName', profileForm.farmName);
         formData.append('cropCategories', JSON.stringify(profileForm.cropCategories));
      }
      if (profilePhoto) {
         formData.append('profilePhoto', profilePhoto);
      }
      const updatedUser = await api.updateProfile(formData);
      
      // Keep currentUser up to date in localStorage for simple context
      const curr = JSON.parse(localStorage.getItem('currentUser') || '{}');
      localStorage.setItem('currentUser', JSON.stringify({ ...curr, ...updatedUser }));
      // Also authContext uses 'user' sometimes? Wait, usually contexts read from localStorage or we just reload
      
      toast({
        title: 'Profile Updated',
        description: 'Your profile has been successfully updated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reload page to reflect changes
      setTimeout(() => window.location.reload(), 1000);
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to update profile.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({
        title: 'Error',
        description: 'New passwords do not match.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters long.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await api.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      toast({
        title: 'Password Changed',
        description: 'Your password has been successfully updated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to change password.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Container maxW="container.md" py={8}>
      <VStack spacing={6} align="stretch">
        <Heading size="lg">Profile Settings</Heading>

        <Card>
          <CardBody>
            <Tabs colorScheme="brand">
              <TabList>
                <Tab>Profile Information</Tab>
                <Tab>Change Password</Tab>
              </TabList>

              <TabPanels>
                {/* Profile Tab */}
                <TabPanel>
                  <form onSubmit={handleProfileUpdate}>
                    <VStack spacing={4} align="stretch">
                      <FormControl>
                        <FormLabel>Profile Photo</FormLabel>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            setProfilePhoto(file || null);
                          }}
                          p={1}
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>Full Name</FormLabel>
                        <Input
                          value={profileForm.fullName}
                          onChange={(e) =>
                            setProfileForm({ ...profileForm, fullName: e.target.value })
                          }
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>Email</FormLabel>
                        <Input
                          type="email"
                          value={profileForm.email}
                          onChange={(e) =>
                            setProfileForm({ ...profileForm, email: e.target.value })
                          }
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>Phone Number</FormLabel>
                        <Input
                          type="tel"
                          value={profileForm.phoneNumber}
                          onChange={(e) =>
                            setProfileForm({ ...profileForm, phoneNumber: e.target.value })
                          }
                        />
                      </FormControl>

                      {user?.role === 'farmer' && (
                        <>
                          <Divider my={2} />
                          <Text fontWeight="bold" fontSize="lg">
                            Farm Information
                          </Text>

                          <FormControl>
                            <FormLabel>Farm Name</FormLabel>
                            <Input
                              value={profileForm.farmName}
                              onChange={(e) =>
                                setProfileForm({ ...profileForm, farmName: e.target.value })
                              }
                              placeholder="e.g., Sunrise Farms"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Crop Categories</FormLabel>
                            <Select
                              value={profileForm.cropCategories?.[0] || ''}
                              onChange={(e) =>
                                setProfileForm({ ...profileForm, cropCategories: [e.target.value] })
                              }
                            >
                              <option value="vegetables">Vegetables</option>
                              <option value="fruits">Fruits</option>
                              <option value="grains">Grains</option>
                              <option value="livestock">Livestock</option>
                              <option value="dairy">Dairy</option>
                              <option value="other">Other</option>
                            </Select>
                          </FormControl>

                          <FormControl>
                            <FormLabel>Farm Location</FormLabel>
                            <Input
                              value={profileForm.farmLocation}
                              onChange={(e) =>
                                setProfileForm({ ...profileForm, farmLocation: e.target.value })
                              }
                              placeholder="e.g., Harare, Mashonaland"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Farm Size (hectares)</FormLabel>
                            <Input
                              type="number"
                              value={profileForm.farmSize}
                              onChange={(e) =>
                                setProfileForm({ ...profileForm, farmSize: e.target.value })
                              }
                              placeholder="e.g., 5"
                            />
                          </FormControl>
                        </>
                      )}

                      <Button type="submit" colorScheme="brand" size="lg" mt={4}>
                        Update Profile
                      </Button>
                    </VStack>
                  </form>
                </TabPanel>

                {/* Password Tab */}
                <TabPanel>
                  <form onSubmit={handlePasswordChange}>
                    <VStack spacing={4} align="stretch">
                      <FormControl isRequired>
                        <FormLabel>Current Password</FormLabel>
                        <Input
                          type="password"
                          value={passwordForm.currentPassword}
                          onChange={(e) =>
                            setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                          }
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>New Password</FormLabel>
                        <Input
                          type="password"
                          value={passwordForm.newPassword}
                          onChange={(e) =>
                            setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                          }
                        />
                        <Text fontSize="sm" color="gray.600" mt={1}>
                          Must be at least 6 characters
                        </Text>
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>Confirm New Password</FormLabel>
                        <Input
                          type="password"
                          value={passwordForm.confirmPassword}
                          onChange={(e) =>
                            setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                          }
                        />
                      </FormControl>

                      <Button type="submit" colorScheme="brand" size="lg" mt={4}>
                        Change Password
                      </Button>
                    </VStack>
                  </form>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default Profile;
